import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, InputNumber, Radio, Select } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { CustomButton, CustomTitle } from '../../components';
import {
  AlertThresholds,
  DEFAULT_SCENARIO,
  ExcursionDirection,
  JOURNEY_OPTIONS,
  MAX_BATTERY_THRESHOLD_PCT,
  MAX_DELIVERY_OFFSET_FT,
  MAX_NEARING_MARGIN_PCT,
  MIN_DELIVERY_OFFSET_FT,
  METRIC_INFO,
  METRIC_UNIT,
  PACE_OPTIONS,
  SCENARIO_TYPE_OPTIONS,
  ScenarioMetric,
  ScenarioProduct,
  ScenarioType,
  SimulationScenarioConfig,
  formatMinutes,
  formatProductLimits,
  hasBatteryThreshold,
  hasDirection,
  hasMagnitude,
  hasWindow,
  isBackdated,
  isViability,
  productLimits,
  scenarioMetric,
  scenarioWarnings,
  sizeScenario,
  summarizeScenario,
  thresholdWarnings,
  validateScenario,
} from './simulationScenario';
import './SimulationScenarioView.css';

interface SimulationScenarioViewProps {
  value: SimulationScenarioConfig | null;
  /** Products with limits for the metric that match a search ('' lists them all). */
  loadProducts: (
    search: string,
    metric: ScenarioMetric
  ) => Promise<ScenarioProduct[]>;
  /** The company's alert settings by scenario type; null until they load or when unavailable. */
  thresholds?: AlertThresholds | null;
  onSave: (config: SimulationScenarioConfig) => void;
  onCancel: () => void;
}

const DEFAULT_HOURS_AGO = 3;
const PRODUCT_SEARCH_DELAY_MS = 300;

/**
 * Choose how a ZoomLogi Simulator shipment plays out: the scenario, when it
 * starts and how fast, and its details. Saved back into the create drawer.
 */
const SimulationScenarioView: React.FC<SimulationScenarioViewProps> = ({
  value,
  loadProducts,
  thresholds = null,
  onSave,
  onCancel,
}) => {
  const [draft, setDraft] = useState<SimulationScenarioConfig>(
    value ?? DEFAULT_SCENARIO
  );
  const errors = validateScenario(draft);
  const warnings = [
    ...scenarioWarnings(draft),
    ...thresholdWarnings(draft, thresholds),
  ];
  const startsInPast = draft.started_minutes_ago > 0;

  const type = draft.scenario_type;
  const metric = scenarioMetric(type);
  const [productOptions, setProductOptions] = useState<ScenarioProduct[]>(
    value?.product ? [value.product] : []
  );
  const [productSearch, setProductSearch] = useState('');
  // True once the whole catalog (an empty search) has no product with limits.
  const [noQualifyingProduct, setNoQualifyingProduct] = useState(false);
  const loadProductsRef = useRef(loadProducts);
  loadProductsRef.current = loadProducts;
  const productRange = productLimits(draft.product, metric);

  useEffect(() => {
    if (metric === null) {
      setNoQualifyingProduct(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      loadProductsRef
        .current(productSearch.trim(), metric)
        .then((products) => {
          if (cancelled) return;
          setProductOptions(products);
          if (productSearch.trim() === '') {
            setNoQualifyingProduct(products.length === 0);
          }
        })
        .catch(() => {
          if (!cancelled) setProductOptions([]);
        });
    }, PRODUCT_SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [productSearch, metric]);

  // A new scenario is sized to the company's alert settings once they arrive.
  const startedFromSaved = useRef(value !== null);
  useEffect(() => {
    if (thresholds && !startedFromSaved.current) {
      setDraft((prev) => sizeScenario(prev, thresholds));
    }
  }, [thresholds]);

  const update = (patch: Partial<SimulationScenarioConfig>): void =>
    setDraft((prev) => ({ ...prev, ...patch }));
  /** Temperature limits come from the product; humidity limits are read from it. */
  const withProduct = (
    prev: SimulationScenarioConfig,
    product: ScenarioProduct | null
  ): SimulationScenarioConfig => {
    const range = productLimits(product, 'temperature');
    return {
      ...prev,
      product,
      ...(range && scenarioMetric(prev.scenario_type) === 'temperature'
        ? { temperature_min: range.min, temperature_max: range.max }
        : {}),
    };
  };
  const chooseProduct = (productId: string | undefined): void => {
    const product = productOptions.find((p) => p.id === productId) ?? null;
    // A viability scenario's start and length follow the product's window.
    setDraft((prev) => {
      const next = withProduct(prev, product);
      return isViability(next.scenario_type)
        ? sizeScenario(next, thresholds)
        : next;
    });
  };
  /**
   * A product stays only if it has limits for the new scenario's metric, the
   * incident window starts over, and the result is sized to the alert settings.
   */
  const chooseScenarioType = (scenario_type: ScenarioType): void => {
    setProductSearch('');
    startedFromSaved.current = true;
    setDraft((prev) => {
      const nextMetric = scenarioMetric(scenario_type);
      const direction: ExcursionDirection =
        nextMetric !== null && METRIC_INFO[nextMetric].maxOnly
          ? 'high'
          : prev.excursion.direction;
      // A back-dated scenario sets its own start, journey and pace; leaving one
      // (or entering one) starts those over.
      const startsOver =
        isBackdated(prev.scenario_type) ||
        isBackdated(scenario_type) ||
        isViability(prev.scenario_type) ||
        isViability(scenario_type);
      const next: SimulationScenarioConfig = {
        ...prev,
        scenario_type,
        ...(startsOver
          ? {
              started_minutes_ago: DEFAULT_SCENARIO.started_minutes_ago,
              pace: DEFAULT_SCENARIO.pace,
              journey_minutes: DEFAULT_SCENARIO.journey_minutes,
            }
          : {}),
        excursion: {
          ...prev.excursion,
          direction,
          start_offset_min: DEFAULT_SCENARIO.excursion.start_offset_min,
          duration_min: DEFAULT_SCENARIO.excursion.duration_min,
        },
      };
      const keeps = productLimits(prev.product, nextMetric) !== null;
      return sizeScenario(
        withProduct(next, keeps ? prev.product : null),
        thresholds
      );
    });
  };
  const updateExcursion = (
    patch: Partial<SimulationScenarioConfig['excursion']>
  ): void =>
    setDraft((prev) => ({
      ...prev,
      excursion: { ...prev.excursion, ...patch },
    }));

  return (
    <div>
      <div className='drawer-title-header simulation-scenario-header'>
        <Button
          type='text'
          className='simulation-scenario-back'
          aria-label='Back to shipment'
          icon={<ArrowLeftOutlined />}
          onClick={onCancel}
        />
        <CustomTitle
          text='Simulator scenario'
          fontSize={20}
          fontWeight='700'
          lineHeight='32px'
          color='var(--text-primary)'
          marginBottom={0}
        />
      </div>

      <div className='simulation-scenario-view'>
        <div className='simulation-scenario-field'>
          <span className='simulation-scenario-label'>Scenario</span>
          <Select<ScenarioType>
            aria-label='Scenario'
            // About twenty rows: render them all so they can be found and read.
            virtual={false}
            listHeight={400}
            value={draft.scenario_type}
            options={SCENARIO_TYPE_OPTIONS}
            onChange={chooseScenarioType}
          />
        </div>

        {metric !== null ? (
          <div className='simulation-scenario-field'>
            <span className='simulation-scenario-label'>Product</span>
            <Select<string>
              aria-label='Product'
              showSearch
              allowClear
              filterOption={false}
              placeholder={`Select a product with ${metric} limits`}
              notFoundContent={`No products with ${metric} thresholds`}
              value={draft.product?.id}
              options={[
                ...(draft.product &&
                !productOptions.some((p) => p.id === draft.product?.id)
                  ? [draft.product]
                  : []),
                ...productOptions,
              ].map((p) => ({ label: p.name, value: p.id, product: p }))}
              optionRender={(option) => (
                <div className='simulation-scenario-product-option'>
                  <span>{option.data.product.name}</span>
                  <span className='simulation-scenario-hint'>
                    {formatProductLimits(option.data.product, metric)}
                  </span>
                </div>
              )}
              onSearch={setProductSearch}
              onChange={chooseProduct}
            />
            {noQualifyingProduct ? (
              <Alert
                type='warning'
                showIcon
                message={`No qualifying product found. Please add a product with ${metric} thresholds in Settings → Products.`}
              />
            ) : null}
          </div>
        ) : null}

        <div className='simulation-scenario-field'>
          <span className='simulation-scenario-label'>Transit length</span>
          <Select<number>
            aria-label='Transit length'
            value={draft.journey_minutes}
            options={
              JOURNEY_OPTIONS.some((o) => o.value === draft.journey_minutes)
                ? JOURNEY_OPTIONS
                : [
                    ...JOURNEY_OPTIONS,
                    {
                      label: formatMinutes(draft.journey_minutes),
                      value: draft.journey_minutes,
                    },
                  ].sort((a, b) => a.value - b.value)
            }
            onChange={(journey_minutes) => update({ journey_minutes })}
          />
        </div>

        <div className='simulation-scenario-field'>
          <span className='simulation-scenario-label'>Start</span>
          <Radio.Group
            value={startsInPast ? 'past' : 'now'}
            onChange={(event) =>
              update(
                event.target.value === 'now'
                  ? { started_minutes_ago: 0, pace: 1 }
                  : { started_minutes_ago: DEFAULT_HOURS_AGO * 60 }
              )
            }
          >
            <Radio value='now'>Now</Radio>
            <Radio value='past'>In the past</Radio>
          </Radio.Group>
        </div>

        {startsInPast ? (
          <div className='simulation-scenario-row'>
            <div className='simulation-scenario-field'>
              <span className='simulation-scenario-label'>
                Started (hours ago)
              </span>
              <InputNumber
                aria-label='Started hours ago'
                min={1}
                max={168}
                precision={0}
                value={Math.round(draft.started_minutes_ago / 60)}
                onChange={(hours) =>
                  update({ started_minutes_ago: Math.max(hours ?? 1, 1) * 60 })
                }
              />
            </div>
            <div className='simulation-scenario-field'>
              <span className='simulation-scenario-label'>Pace</span>
              <Select<number>
                aria-label='Pace'
                value={draft.pace}
                options={PACE_OPTIONS}
                onChange={(pace) => update({ pace })}
              />
            </div>
          </div>
        ) : null}

        {metric !== null ? (
          <div className='simulation-scenario-row'>
            {METRIC_INFO[metric].maxOnly ? null : (
              <div className='simulation-scenario-field'>
                <span className='simulation-scenario-label'>
                  {`Minimum ${metric} (${METRIC_UNIT[metric]})`}
                </span>
                <InputNumber
                  aria-label={`Minimum ${metric}`}
                  disabled
                  value={productRange?.min}
                />
              </div>
            )}
            <div className='simulation-scenario-field'>
              <span className='simulation-scenario-label'>
                {metric === 'viability'
                  ? 'Viability window (hours)'
                  : `Maximum ${metric} (${METRIC_UNIT[metric]})`}
              </span>
              <InputNumber
                aria-label={
                  metric === 'viability'
                    ? 'Viability window'
                    : `Maximum ${metric}`
                }
                disabled
                value={productRange?.max}
              />
            </div>
          </div>
        ) : null}

        {hasBatteryThreshold(type) ? (
          <div className='simulation-scenario-row'>
            <div className='simulation-scenario-field'>
              <span className='simulation-scenario-label'>
                Battery threshold (%)
              </span>
              <InputNumber
                aria-label='Battery threshold'
                disabled
                max={MAX_BATTERY_THRESHOLD_PCT}
                value={draft.battery_threshold_pct}
              />
            </div>
            {type === 'low_battery' ? (
              <div className='simulation-scenario-field'>
                <span className='simulation-scenario-label'>
                  Falls below (min after pickup)
                </span>
                <InputNumber
                  aria-label='Minutes after pickup'
                  min={0}
                  precision={0}
                  value={draft.battery_low_offset_min}
                  onChange={(v) => update({ battery_low_offset_min: v ?? 0 })}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {type === 'incorrect_delivery_location' ? (
          <div className='simulation-scenario-field'>
            <span className='simulation-scenario-label'>
              Sensor readings are this far from the destination (feet)
            </span>
            <InputNumber
              aria-label='Delivery distance'
              min={MIN_DELIVERY_OFFSET_FT}
              max={MAX_DELIVERY_OFFSET_FT}
              precision={0}
              value={draft.delivery_offset_ft}
              onChange={(v) => update({ delivery_offset_ft: v ?? 0 })}
            />
          </div>
        ) : null}

        {metric !== null && hasDirection(type) ? (
          <div className='simulation-scenario-field'>
            <span className='simulation-scenario-label'>
              {type === 'temperature_nearing_limit' ? 'Near' : 'Excursion'}
            </span>
            <Select<ExcursionDirection>
              aria-label='Excursion direction'
              value={draft.excursion.direction}
              options={
                type === 'temperature_nearing_limit'
                  ? [
                      { label: 'Near the maximum', value: 'high' },
                      { label: 'Near the minimum', value: 'low' },
                    ]
                  : [
                      {
                        label: METRIC_INFO[metric].directionLabels.high,
                        value: 'high',
                      },
                      {
                        label: METRIC_INFO[metric].directionLabels.low,
                        value: 'low',
                      },
                    ]
              }
              onChange={(direction) => updateExcursion({ direction })}
            />
          </div>
        ) : null}

        {type === 'temperature_nearing_limit' ? (
          <div className='simulation-scenario-field'>
            <span className='simulation-scenario-label'>
              Alert margin (% of range)
            </span>
            <InputNumber
              aria-label='Alert margin'
              disabled
              max={MAX_NEARING_MARGIN_PCT}
              value={draft.margin_pct}
            />
          </div>
        ) : null}

        {metric !== null && hasMagnitude(type) ? (
          <div className='simulation-scenario-field'>
            <span className='simulation-scenario-label'>
              {METRIC_INFO[metric].magnitudeLabel}
            </span>
            <InputNumber
              aria-label={METRIC_INFO[metric].magnitudeLabel}
              min={0.5}
              max={METRIC_INFO[metric].maxMagnitude}
              step={0.5}
              value={draft.excursion[METRIC_INFO[metric].magnitudeKey]}
              onChange={(v) =>
                updateExcursion({ [METRIC_INFO[metric].magnitudeKey]: v ?? 0 })
              }
            />
          </div>
        ) : null}

        {hasWindow(type) ? (
          <div className='simulation-scenario-row'>
            <div className='simulation-scenario-field'>
              <span className='simulation-scenario-label'>
                Starts (min after pickup)
              </span>
              <InputNumber
                aria-label='Minutes after pickup'
                min={0}
                precision={0}
                value={draft.excursion.start_offset_min}
                onChange={(v) => updateExcursion({ start_offset_min: v ?? 0 })}
              />
            </div>
            <div className='simulation-scenario-field'>
              <span className='simulation-scenario-label'>Lasts (minutes)</span>
              <InputNumber
                aria-label='Excursion duration'
                min={1}
                precision={0}
                value={draft.excursion.duration_min}
                onChange={(v) => updateExcursion({ duration_min: v ?? 0 })}
              />
            </div>
          </div>
        ) : null}

        <div className='simulation-scenario-summary'>
          {summarizeScenario(draft).map((line) => (
            <span key={line}>{line}</span>
          ))}
        </div>

        {errors.map((error) => (
          <Alert key={error} type='error' showIcon message={error} />
        ))}
        {warnings.map((warning) => (
          <Alert key={warning} type='warning' showIcon message={warning} />
        ))}

        <div className='simulation-scenario-actions'>
          <CustomButton
            text='Save scenario'
            width='100%'
            height='48px'
            borderRadius='8px'
            bgColor='#000'
            color='#fff'
            border='0px'
            fontSize='16px'
            fontWeight={600}
            disabled={errors.length > 0}
            onClick={() => onSave(draft)}
          />
          <CustomButton
            text='Cancel'
            width='100%'
            height='48px'
            bgColor='transparent'
            color='black'
            fontSize='16px'
            fontWeight='600'
            boxShadow='none'
            onClick={onCancel}
          />
        </div>
      </div>
    </div>
  );
};

export default SimulationScenarioView;
